/**
 * CRM réseau — clients FINAUX agrégés (SuperAdmin, brique B) — LOGIQUE PURE.
 *
 * Consolide les consommateurs de tous les restos partenaires en une base réseau :
 * qui ils sont, quels restos ils fréquentent, combien ils ont commandé/dépensé,
 * et leur consentement (réseau Lil & Co vs resto). Aucune I/O ici (cf. networkCrmService.ts).
 * La donnée vient de la RPC `list_network_customers` (customers + customer_tenants + tenants).
 */

/** Lien client ↔ resto fréquenté (extrait de customer_tenants). */
export interface TenantLink {
  tenantId: string;
  name: string;
  slug: string;
  marketingConsent: boolean;
  totalOrders: number;
  totalSpentCents: number;
  lastOrderAt: string | null;
}

/** Client réseau enrichi (assemblé depuis la RPC). */
export interface NetworkCustomer {
  id: string;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  /** Opt-in communication réseau Lil & Co (cross-resto). */
  networkConsent: boolean;
  createdAt: string | null;
  lastOrderAt: string | null;
  /** Restos fréquentés (au moins une commande). */
  tenants: TenantLink[];
}

/** Forme brute renvoyée par la RPC (jsonb). */
interface RawTenantLink {
  tenant_id?: string;
  name?: string | null;
  slug?: string | null;
  marketing_consent?: boolean | null;
  total_orders?: number | null;
  total_spent_cents?: number | null;
  last_order_at?: string | null;
}
interface RawCustomer {
  id?: string;
  email?: string | null;
  phone?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  network_consent?: boolean | null;
  created_at?: string | null;
  last_order_at?: string | null;
  tenants?: RawTenantLink[] | null;
}

/** Normalise une ligne brute de la RPC en NetworkCustomer typé et sûr. */
export function toNetworkCustomer(raw: RawCustomer): NetworkCustomer {
  return {
    id: raw.id ?? '',
    email: raw.email ?? '',
    phone: raw.phone ?? '',
    firstName: raw.first_name ?? '',
    lastName: raw.last_name ?? '',
    networkConsent: raw.network_consent === true,
    createdAt: raw.created_at ?? null,
    lastOrderAt: raw.last_order_at ?? null,
    tenants: (raw.tenants ?? []).map((t) => ({
      tenantId: t.tenant_id ?? '',
      name: t.name ?? '',
      slug: t.slug ?? '',
      marketingConsent: t.marketing_consent === true,
      totalOrders: t.total_orders ?? 0,
      totalSpentCents: t.total_spent_cents ?? 0,
      lastOrderAt: t.last_order_at ?? null,
    })),
  };
}

/** Parse la réponse jsonb de la RPC (array, ou null) en liste typée. */
export function parseNetworkCustomers(data: unknown): NetworkCustomer[] {
  if (!Array.isArray(data)) return [];
  return data.map((r) => toNetworkCustomer(r as RawCustomer));
}

/** Nom affichable (prénom + nom), repli sur l'email puis le téléphone. */
export function displayName(c: NetworkCustomer): string {
  const full = `${c.firstName} ${c.lastName}`.trim();
  return full || c.email || c.phone || 'Client sans nom';
}

/** Total de commandes du client tous restos confondus. */
export function totalOrders(c: NetworkCustomer): number {
  return c.tenants.reduce((sum, t) => sum + t.totalOrders, 0);
}

/** Total dépensé (centimes) tous restos confondus. */
export function totalSpentCents(c: NetworkCustomer): number {
  return c.tenants.reduce((sum, t) => sum + t.totalSpentCents, 0);
}

/** Le client fréquente-t-il plusieurs restos du réseau ? (valeur cross-sell). */
export function isMultiResto(c: NetworkCustomer): boolean {
  return c.tenants.length > 1;
}

export interface NetworkCrmFilters {
  /** id de resto, ou 'all'. */
  tenantId?: string;
  /** 'all' | 'network' (opt-in réseau) | 'no-network'. */
  optin?: 'all' | 'network' | 'no-network';
  /** recherche libre (nom, email, téléphone). */
  search?: string;
}

/** Applique les filtres d'affichage à la liste réseau. */
export function filterCustomers(list: NetworkCustomer[], f: NetworkCrmFilters): NetworkCustomer[] {
  const tenantId = f.tenantId ?? 'all';
  const optin = f.optin ?? 'all';
  const search = (f.search ?? '').trim().toLowerCase();

  return list.filter((c) => {
    if (tenantId !== 'all' && !c.tenants.some((t) => t.tenantId === tenantId)) return false;
    if (optin === 'network' && !c.networkConsent) return false;
    if (optin === 'no-network' && c.networkConsent) return false;
    if (search) {
      const hay = `${displayName(c)} ${c.email} ${c.phone}`.toLowerCase();
      if (!hay.includes(search)) return false;
    }
    return true;
  });
}

/** Restos distincts présents dans la base (pour le filtre par resto). */
export function tenantsInData(list: NetworkCustomer[]): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  list.forEach((c) => c.tenants.forEach((t) => seen.set(t.tenantId, t.name)));
  return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

/** Échappe une valeur pour le CSV (guillemets, séparateurs, retours ligne). */
function csvCell(v: string | number): string {
  const s = String(v ?? '');
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Export CSV (pur) de la base clients réseau — séparateur « ; » (compat Excel FR).
 * Colonnes : Nom, Email, Téléphone, Restos fréquentés, Commandes, Dépensé (€),
 * Opt-in réseau, Créé le.
 */
export function customersToCsv(list: NetworkCustomer[]): string {
  const header = [
    'Nom',
    'Email',
    'Téléphone',
    'Restos fréquentés',
    'Commandes',
    'Dépensé (€)',
    'Opt-in réseau',
    'Créé le',
  ];
  const rows = list.map((c) => [
    displayName(c),
    c.email || '',
    c.phone || '',
    c.tenants.map((t) => t.name).join(' / '),
    totalOrders(c),
    (totalSpentCents(c) / 100).toFixed(2).replace('.', ','),
    c.networkConsent ? 'oui' : 'non',
    c.createdAt ? new Date(c.createdAt).toLocaleDateString('fr-FR') : '',
  ]);
  return [header, ...rows].map((r) => r.map(csvCell).join(';')).join('\r\n');
}

/** Statistiques d'en-tête : nb clients, opt-in réseau, nb restos couverts. */
export function networkStats(list: NetworkCustomer[]): {
  customers: number;
  optinNetwork: number;
  tenants: number;
} {
  return {
    customers: list.length,
    optinNetwork: list.filter((c) => c.networkConsent).length,
    tenants: tenantsInData(list).length,
  };
}
